import {
  Box,
  buildPath,
  buildTemplatePath,
  Grid,
  Page,
  PageRoute,
  Stack,
  useElementBreakpoints
} from "@compass/core";
import {
  LkPersonTypeId,
  TextingConversationListResource,
} from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import ApiService from "services/apiService";
import { TextingMessageProvider } from "../NewTextingMessageContext";
import { ChatScreen } from "./ChatScreen/ChatScreen";
import { Conversations } from "./Conversations";
import { PersonTypeTabs } from "./PersonTypeTabs";
import { TextingConversationsPageHeader } from "./TextingConversationsPageHeader";
import { Outlet } from "react-router-dom";
import { notifyTextingUnreadCountChanged } from "../textingEvents";

const routeName = "Conversations";

export const textingConversationsPageRoute: PageRoute = {
  handle: "Conversations",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export type TextingConversationTab = {
  personTypeId: number;
  personType: "Student" | "Parent" | "Staff";
};

export const TextingConversationsPage = (props: { studentId?: number; embedded?: boolean }) => {
  const [personTypeId, setPersonTypeId] = useState<number>(LkPersonTypeId.Student);
  const [selectedConversationId, setSelectedConversationId] = useState<number>();

  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const [resource, setResource] = useState<TextingConversationListResource>({});

  useEffect(() => {
    fetchResource();

    if (!props.embedded) {
      ApiService.textingConversations
        .clearAllUnseenMessages()
        .then(notifyTextingUnreadCountChanged)
        .catch((error) => console.error("Failed to clear unread text message count:", error));
    }
  }, [props.embedded]);

  const fetchResource = () => {
    ApiService.textingConversations.getListResource().then((response) => {
      setResource(response);
    });
  };

  const pageHeader = <TextingConversationsPageHeader handle={textingConversationsPageRoute.handle} />;

  const updatePersonTypeId = (value: number) => {
    setPersonTypeId(value);
    setSelectedConversationId(undefined);
  };

  const content = (
    <Stack ref={breakpointsRef} spacing="md" height="100%">
      {!props.studentId && (
        <PersonTypeTabs personTypes={resource.personTypes ?? []} value={personTypeId} onChange={updatePersonTypeId} />
      )}

      <Box overflow="auto" flexGrow={1} minHeight={0}>
        <Grid container spacing="md" height="100%">
          <Grid item xs={setBreakpoints({ md: 4, xs: 12 })} height="100%">
            <Box height="100%" overflow="auto">
              <Conversations
                personTypeId={personTypeId}
                personId={props.studentId}
                selectedConversationId={selectedConversationId}
                setSelectedConversationId={setSelectedConversationId}
              />
            </Box>
          </Grid>

          {selectedConversationId && (
            <Grid item xs={setBreakpoints({ md: 8, xs: 12 })} height="100%">
              <Box height="100%" overflow="auto">
                <ChatScreen conversationId={selectedConversationId} />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Stack>
  );

  if (props.embedded) {
    return <TextingMessageProvider>{content}</TextingMessageProvider>;
  }

  return (
    <TextingMessageProvider>
      <Page isForm header={pageHeader}>
        {content}
      </Page>
      <Outlet />
    </TextingMessageProvider>
  );
};
